import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppBar,
  Avatar,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import GetAppIcon from "@material-ui/icons/GetApp";
import RotateLeftIcon from "@material-ui/icons/RotateLeft";

import TareasTerminadas from "./TareasTerminadas";
import Paginacion, { OPCIONES_POR_PAGINA } from "./Paginacion";
import { coloresEmma } from "../../global/js/funciones";
import emma_w from "../../global/imagenes/emma_w.png";
import emma_s1 from "../../global/imagenes/emma_s1.png";

const useStyles = makeStyles((theme) => ({
  appBar: {
    width: "100%",
    backgroundColor: "white",
  },
  title: {
    margin: "0 auto",
    color: "#757575",
    display: "none",
    flexGrow: 1,
    [theme.breakpoints.up("sm")]: {
      display: "block",
    },
  },
  search: {
    height: 40,
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: coloresEmma.searchcolor,
    "&:hover": {
      backgroundColor: coloresEmma.serchover,
    },
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(1),
      width: "auto",
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: 40,
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: coloresEmma.secondarycolor,
  },
  inputRoot: {
    height: 30,
  },
  inputInput: {
    height: 40,
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
      "&:focus": {
        width: "20ch",
      },
    },
  },
}));

const esFuncion = (valor) => typeof valor === "function";
const leer = (row, campo) => (esFuncion(campo) ? campo(row) : row[campo]);

const Barra = ({ titulo, busqueda, onBuscar, onRefrescar, onDescargar }) => {
  const classes = useStyles();
  return (
    <AppBar position="static" className={classes.appBar} elevation={0}>
      <Toolbar>
        <Typography variant="subtitle2" className={classes.title}>
          {titulo}
        </Typography>
        {esFuncion(onBuscar) && (
          <div className={classes.search}>
            <div className={classes.searchIcon}>
              <SearchIcon />
            </div>
            <InputBase
              value={busqueda}
              onChange={({ target }) => onBuscar(target.value)}
              placeholder="Buscar"
              classes={{ root: classes.inputRoot, input: classes.inputInput }}
              inputProps={{ "aria-label": "search" }}
            />
          </div>
        )}
        {esFuncion(onRefrescar) && (
          <Tooltip title="Refrescar">
            <IconButton edge="end" color="secondary" onClick={onRefrescar}>
              <RotateLeftIcon />
            </IconButton>
          </Tooltip>
        )}
        {esFuncion(onDescargar) && (
          <Tooltip title="Descargar">
            <IconButton edge="end" color="secondary" onClick={onDescargar}>
              <GetAppIcon />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
    </AppBar>
  );
};

const ListarDatosPaginados = ({
  datos,
  titulo,
  fila_principal,
  filas,
  avatar,
  color_avatar,
  acciones,
  mensajeVacio,
  cargando,
  total,
  pagina,
  porPagina,
  opcionesPorPagina,
  onCambiarPagina,
  onCambiarPorPagina,
  busqueda,
  onBuscar,
  onRefrescar,
  onDescargar,
}) => {
  const [seleccionado, setSeleccionado] = useState(null);
  const tituloCompleto = `${titulo} - Mostrando ${datos.length} registros de un total de ${total}.`;

  return (
    <div>
      <List
        className="scroll"
        subheader={
          <ListSubheader
            component="div"
            style={{
              backgroundColor: "white",
              borderBottom: "1px solid #eeeeee",
            }}
          >
            <Barra
              titulo={tituloCompleto}
              busqueda={busqueda}
              onBuscar={onBuscar}
              onRefrescar={onRefrescar}
              onDescargar={onDescargar}
            />
          </ListSubheader>
        }
      >
        {cargando || datos.length === 0 ? (
          <TareasTerminadas
            imagen={cargando ? emma_w : emma_s1}
            mensaje={cargando ? "Cargando.." : mensajeVacio}
            widthImg="7%"
            marginTop="7%"
            cargando={cargando}
          />
        ) : (
          datos.map((row, i) => (
            <ListItem
              key={row.id ?? i}
              onClick={() => setSeleccionado(i)}
              style={{
                backgroundColor:
                  seleccionado === i ? coloresEmma.select : "white",
                borderBottom: "1px solid #eeeeee",
              }}
            >
              {avatar && (
                <ListItemAvatar>
                  <Avatar
                    style={
                      esFuncion(color_avatar) ? color_avatar(row) : color_avatar
                    }
                  >
                    {leer(row, avatar)}
                  </Avatar>
                </ListItemAvatar>
              )}
              <ListItemText
                primary=""
                secondary={
                  <>
                    <Typography
                      component="span"
                      variant="body2"
                      style={{ color: "black" }}
                    >
                      {leer(row, fila_principal)}
                      <br />
                    </Typography>
                    {filas
                      .filter(({ enLista = true }) => enLista)
                      .map(({ id, mostrar, nombre = "" }) => {
                        const valor = mostrar ? mostrar(row) : row[id];
                        return valor ? (
                          <Typography
                            key={id}
                            component="span"
                            variant="body2"
                            color="textSecondary"
                          >
                            {nombre}
                            {valor}
                            <br />
                          </Typography>
                        ) : null;
                      })}
                  </>
                }
              />
              {esFuncion(acciones) && (
                <ListItemSecondaryAction
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  {acciones(row)}
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))
        )}
      </List>
      <Paginacion
        total={total}
        pagina={pagina}
        porPagina={porPagina}
        opciones={opcionesPorPagina}
        onCambiarPagina={onCambiarPagina}
        onCambiarPorPagina={onCambiarPorPagina}
      />
    </div>
  );
};

ListarDatosPaginados.propTypes = {
  datos: PropTypes.array.isRequired,
  titulo: PropTypes.string.isRequired,
  fila_principal: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
    .isRequired,
  filas: PropTypes.array,
  avatar: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  color_avatar: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  acciones: PropTypes.func,
  mensajeVacio: PropTypes.string,
  cargando: PropTypes.bool,
  total: PropTypes.number.isRequired,
  pagina: PropTypes.number.isRequired,
  porPagina: PropTypes.number.isRequired,
  opcionesPorPagina: PropTypes.arrayOf(PropTypes.number),
  onCambiarPagina: PropTypes.func.isRequired,
  onCambiarPorPagina: PropTypes.func.isRequired,
  busqueda: PropTypes.string,
  onBuscar: PropTypes.func,
  onRefrescar: PropTypes.func,
  onDescargar: PropTypes.func,
};

ListarDatosPaginados.defaultProps = {
  filas: [],
  avatar: "",
  color_avatar: () => ({
    backgroundColor: coloresEmma.fondocolor,
    color: "#757575",
  }),
  acciones: null,
  mensajeVacio: "Esta bandeja esta vacía",
  cargando: false,
  opcionesPorPagina: OPCIONES_POR_PAGINA,
  busqueda: "",
  onBuscar: null,
  onRefrescar: null,
  onDescargar: null,
};

export default ListarDatosPaginados;
